import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Instagram, Mail } from "lucide-react";
import { CONTACT } from "../data/boats";

const NAV = [
  { label: "Our Houseboats", href: "/#boats" },
  { label: "Experience", href: "/#experience" },
  { label: "Location", href: "/#location" },
  { label: "Contact", href: "/#contact" },
];

export default function Header({ solid = false }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(solid);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(solid || window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [solid]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FAFAF8] border-b border-[#1C1C1A]/10 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-5 flex items-center justify-between gap-6">
        {/* Logo */}
        <Link to="/" data-testid="header-logo" className={`flex items-center gap-3 shrink-0 z-10 transition-colors ${scrolled ? "text-[#1C1C1A]" : "text-white"}`}>
          {/* Inline SVG mark — always crisp, colour follows header state */}
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="18" cy="18" r="16.5" stroke="currentColor" strokeWidth="1.4"/>
            {/* sun */}
            <circle cx="24.5" cy="10.5" r="2.4" fill="currentColor"/>
            {/* wave 1 */}
            <path d="M4 17 Q8 13.5 12 17 Q16 20.5 20 17 Q24 13.5 32 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
            {/* wave 2 */}
            <path d="M4 21.5 Q8 18 12 21.5 Q16 25 20 21.5 Q24 18 32 21.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
            {/* wave 3 */}
            <path d="M6 26 Q10 22.5 14 26 Q18 29.5 22 26 Q26 22.5 31 26" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
          </svg>
          <span className="font-serif text-2xl font-normal tracking-wide">Aquastays</span>
        </Link>

        {/* Desktop nav — centred */}
        <nav className="hidden lg:flex items-center gap-10 absolute left-1/2 -translate-x-1/2">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-[0.68rem] font-medium uppercase tracking-[0.18em] transition-colors ${
                scrolled ? "text-[#1C1C1A] hover:text-[#C8A96E]" : "text-white/90 hover:text-white"
              }`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* Right side actions */}
        <div className="hidden md:flex items-center gap-5">
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid="header-email-icon"
            aria-label="Email"
            className={`transition-colors ${scrolled ? "text-[#1C1C1A]/60 hover:text-[#1C1C1A]" : "text-white/70 hover:text-white"}`}
          >
            <Mail size={16} strokeWidth={1.5} />
          </a>
          <a
            href="https://www.instagram.com/aqua_stays"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className={`transition-colors ${scrolled ? "text-[#1C1C1A]/60 hover:text-[#1C1C1A]" : "text-white/70 hover:text-white"}`}
          >
            <Instagram size={16} strokeWidth={1.5} />
          </a>
          <a
            href="/#booking"
            data-testid="header-book-cta"
            className="text-[0.68rem] font-medium uppercase tracking-[0.18em] px-5 py-2.5 rounded-full bg-[#E8970A] text-white hover:bg-[#D08500] transition-colors"
          >
            Book now
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="header-mobile-toggle"
          aria-label="Toggle menu"
          className={`lg:hidden p-2 z-10 ${scrolled ? "text-[#1C1C1A]" : "text-white"}`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#FAFAF8] border-t border-[#1C1C1A]/10">
          <div className="px-6 py-8 flex flex-col gap-6">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[0.68rem] font-medium uppercase tracking-[0.18em] text-[#1C1C1A]"
              >
                {item.label}
              </a>
            ))}
            <a href="/#booking" data-testid="mobile-book-cta" className="btn-accent mt-2 w-fit">
              Book now
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
